import React, { useMemo, useRef, useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { ChevronDown, Search } from "lucide-react";
import {
  COUNTRIES,
  DEFAULT_COUNTRY_CODE,
  splitE164,
  filterCountries,
} from "./phoneCountries";

/*
 * PhoneInput — international phone input with global country list + search.
 *
 * • Country pill on the LEFT (RTL-natural placement) with flag + dial-code.
 * • Local-number Input on the RIGHT (English digits only, dir="ltr").
 * • Defaults to Saudi Arabia (+966). Dropdown lists every ISO-3166 country.
 * • Search input at the top filters by name / English alias / ISO-2 / dial code.
 * • Always emits an E.164 string via `onChange(e164OrEmpty)` so the backend
 *   regex `^\+\d{8,15}$` keeps working for every country.
 */

const onlyDigits = (s) => (s || "").replace(/\D+/g, "");

// E.164 caps total length at 15. We subtract the dial code's digit count and
// cap the local portion at the remainder so the picker can't produce strings
// that the backend regex would reject.
const E164_MAX_LOCAL = (dial) => 15 - onlyDigits(dial).length;

const PhoneInput = ({
  id,
  value,
  onChange,
  required,
  testId,
  placeholder = "5XXXXXXXX",
  className = "",
}) => {
  const initial = useMemo(() => {
    if (value) return splitE164(value);
    return {
      country: COUNTRIES.find((c) => c.code === DEFAULT_COUNTRY_CODE) || COUNTRIES[0],
      local: "",
    };
  }, []); // hydrate once
  const [country, setCountry] = useState(initial.country);
  const [local, setLocal] = useState(initial.local);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapRef = useRef(null);
  const searchRef = useRef(null);

  // Close dropdown on outside click / Escape
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    // Auto-focus the search input when the panel opens.
    setTimeout(() => searchRef.current?.focus(), 0);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const filtered = useMemo(() => filterCountries(query), [query]);

  const emit = (c, l) => {
    if (!onChange) return;
    onChange(l ? `${c.dial}${l}` : "");
  };

  const handleSelect = (c) => {
    setCountry(c);
    setOpen(false);
    setQuery("");
    // Re-clamp local if the new country has a shorter max-local budget.
    const clamped = local.slice(0, E164_MAX_LOCAL(c.dial));
    if (clamped !== local) setLocal(clamped);
    emit(c, clamped);
  };

  const handleLocalChange = (e) => {
    let digits = onlyDigits(e.target.value);
    if (digits.startsWith("0")) digits = digits.slice(1); // strip national 0
    digits = digits.slice(0, E164_MAX_LOCAL(country.dial));
    setLocal(digits);
    emit(country, digits);
  };

  return (
    <div
      ref={wrapRef}
      className={`relative flex items-stretch gap-2 ${className}`}
      dir="rtl"
      data-testid={testId ? `${testId}-wrap` : undefined}
    >
      {/* Country selector — fixed-width pill on the LEFT in RTL */}
      <button
        type="button"
        aria-label="رمز الدولة"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        data-testid={testId ? `${testId}-country` : undefined}
        className="order-2 inline-flex items-center gap-1.5 px-3 h-10 rounded-2xl border border-[#E5E1D8] bg-white text-sm text-[#222222] hover:border-[#0A4D3C] focus:outline-none focus:ring-1 focus:ring-[#C5A059] transition-colors shrink-0"
      >
        <span className="text-base leading-none" aria-hidden="true">
          {country.flag}
        </span>
        <span dir="ltr" className="font-mono">{country.dial}</span>
        <ChevronDown className="w-3.5 h-3.5 text-[#5E5C58]" />
      </button>

      {/* Local-number field */}
      <Input
        id={id}
        type="tel"
        inputMode="numeric"
        autoComplete="tel-national"
        value={local}
        onChange={handleLocalChange}
        placeholder={placeholder}
        dir="ltr"
        required={required}
        data-testid={testId ? `${testId}-local` : undefined}
        className="order-1 flex-1 font-mono"
      />

      {open && (
        <div
          data-testid={testId ? `${testId}-panel` : undefined}
          className="absolute z-30 top-12 right-0 w-72 rounded-2xl border border-[#E5E1D8] bg-white shadow-lg overflow-hidden"
        >
          {/* Search filter */}
          <div className="p-2 border-b border-[#EFEAE0]">
            <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-xl bg-[#F6F4EE]">
              <Search className="w-4 h-4 text-[#5E5C58] shrink-0" />
              <input
                ref={searchRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="ابحث عن دولة أو رمز اتصال..."
                data-testid={testId ? `${testId}-search` : undefined}
                className="flex-1 bg-transparent border-0 outline-none text-sm placeholder:text-[#8B8780]"
              />
            </div>
          </div>

          {/* Country list */}
          <ul
            role="listbox"
            data-testid={testId ? `${testId}-list` : undefined}
            className="max-h-72 overflow-auto py-1"
          >
            {filtered.length === 0 && (
              <li className="px-4 py-6 text-center text-xs text-[#8B8780]">
                لا توجد نتائج
              </li>
            )}
            {filtered.map((c) => {
              const active = c.code === country.code;
              return (
                <li key={c.code}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={active}
                    onClick={() => handleSelect(c)}
                    data-testid={testId ? `${testId}-opt-${c.code}` : undefined}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-right ${
                      active
                        ? "bg-[#F6F4EE] text-[#0A4D3C]"
                        : "hover:bg-[#FAF9F6] text-[#222222]"
                    }`}
                  >
                    <span className="text-base shrink-0" aria-hidden="true">
                      {c.flag}
                    </span>
                    <span className="flex-1 truncate">{c.name}</span>
                    <span
                      dir="ltr"
                      className="font-mono text-xs text-[#5E5C58]"
                    >
                      {c.dial}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Hidden mirror so native submit / a11y tools see the combined value. */}
      <input type="hidden" name={id} value={local ? `${country.dial}${local}` : ""} />
    </div>
  );
};

export default PhoneInput;
